import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { formatDuration } from "@/lib/api";
import EntryEditor from "./week-calendar/EntryEditor";
import DayColumn from "./week-calendar/DayColumn";
import {
  DEFAULT_HOUR_PX, MIN_HOUR_PX, MAX_HOUR_PX, SNAP_MIN, HOURS, DAYS_LABELS,
  startOfWeek, addDays, sameDay,
} from "./week-calendar/utils";

export default function WeekCalendar({
  entries, clients, activities, tags, runningEntry,
  onCreate, onUpdate, onDelete, onResume,
  weekStart, daysToShow, workHours, workingDays,
  onCreateClient, onCreateActivity,
  publicHolidays, officeClosures,
}) {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const containerRef = useRef(null);

  // Zoom (pixels per hour). Ctrl/Cmd + wheel to zoom, or the +/- controls in
  // the day-headers row.
  const [hourPx, setHourPx] = useState(DEFAULT_HOUR_PX);

  // Drag-to-create state
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);

  // Current-time tick (re-render every second for live running block)
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const days = useMemo(
    () => Array.from({ length: daysToShow }, (_, i) => addDays(weekStart, i)),
    [weekStart, daysToShow],
  );

  const gridTemplate = useMemo(
    () => `64px repeat(${daysToShow}, minmax(0,1fr))`,
    [daysToShow],
  );

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const activityMap = useMemo(() => Object.fromEntries(activities.map((a) => [a.id, a])), [activities]);
  const tagMap = useMemo(() => Object.fromEntries((tags || []).map((t) => [t.id, t])), [tags]);

  // Scroll to 6 AM on mount. The flex container's clientHeight/scrollHeight
  // may not be finalised in the first paint frame, so we use a ResizeObserver
  // to catch the moment the container becomes scrollable (scrollHeight >
  // clientHeight) and set scrollTop then. Runs at most once.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let done = false;
    const trySet = () => {
      if (done || !containerRef.current) return;
      const c = containerRef.current;
      if (c.scrollHeight > c.clientHeight) {
        c.scrollTop = 6 * hourPx;
        done = true;
      }
    };
    // Attempt sync + next frame + ResizeObserver (covers all timing edge cases).
    trySet();
    const raf = requestAnimationFrame(trySet);
    const ro = new ResizeObserver(trySet);
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);

  // Ctrl/Cmd + wheel = zoom. Preserves the time-of-day under the cursor.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const anchorPx = e.clientY - rect.top + el.scrollTop;
      setHourPx((prev) => {
        const next = Math.max(MIN_HOUR_PX, Math.min(MAX_HOUR_PX, prev + (e.deltaY < 0 ? 8 : -8)));
        if (next !== prev) {
          const minutes = (anchorPx / prev) * 60;
          requestAnimationFrame(() => {
            if (containerRef.current) {
              containerRef.current.scrollTop = (minutes / 60) * next - (e.clientY - rect.top);
            }
          });
        }
        return next;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const yToSnappedMin = (y) => {
    const totalMin = (y / hourPx) * 60;
    return Math.max(0, Math.min(24 * 60, Math.round(totalMin / SNAP_MIN) * SNAP_MIN));
  };

  const beginDrag = (day, dayIndex, e) => {
    if (e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const startMin = yToSnappedMin(y);
    const info = { day, dayIndex, startMin, endMin: startMin, columnTop: rect.top, columnEl: e.currentTarget };
    dragRef.current = info;
    setDrag({ dayIndex, startMin, endMin: startMin });
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (ev) => {
      const info = dragRef.current;
      if (!info) return;
      const rect = info.columnEl.getBoundingClientRect();
      const y = ev.clientY - rect.top;
      const endMin = yToSnappedMin(y);
      setDrag({ dayIndex: info.dayIndex, startMin: info.startMin, endMin });
    };
    const onUp = () => {
      const info = dragRef.current;
      if (!info) return;
      const startMin = info.startMin;
      const endMin = dragRef.current?.lastEndMin ?? startMin;
      dragRef.current = null;

      const a = Math.min(startMin, endMin);
      const b = Math.max(startMin, endMin);
      const span = b - a < SNAP_MIN ? 60 : (b - a);
      const startDt = new Date(info.day);
      startDt.setHours(0, 0, 0, 0);
      startDt.setMinutes(a);
      const endDt = new Date(startDt.getTime() + span * 60 * 1000);
      const endOfDay = new Date(info.day); endOfDay.setHours(23, 59, 59, 999);
      if (endDt > endOfDay) endDt.setTime(endOfDay.getTime());

      const finalEndMin = Math.floor((endDt - startDt) / 60000) + a;
      setDrag({ dayIndex: info.dayIndex, startMin: a, endMin: finalEndMin, locked: true });

      setDraft({
        description: "",
        client_id: null,
        activity_id: null,
        start: startDt.toISOString(),
        end: endDt.toISOString(),
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [hourPx]);

  useEffect(() => {
    if (dragRef.current && drag) dragRef.current.lastEndMin = drag.endMin;
  }, [drag]);

  const dayTotals = useMemo(() => {
    const totals = {};
    for (const d of days) totals[d.toDateString()] = 0;
    for (const e of entries) {
      if (!e.end) continue;
      const ds = new Date(e.start).toDateString();
      if (totals[ds] !== undefined) totals[ds] += e.duration_seconds || 0;
    }
    return totals;
  }, [entries, days]);

  const handleSave = async (data) => {
    if (editing) await onUpdate(editing.id, data);
    else await onCreate(data);
    setEditing(null);
    setDraft(null);
    setDrag(null);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Day headers */}
      <div className="grid border-b border-slate-200 bg-white sticky top-16 z-20"
           style={{ gridTemplateColumns: gridTemplate }}>
        {/* Time gutter with zoom controls */}
        <div className="border-r border-slate-100 flex items-center justify-center gap-0.5 px-1 py-1.5"
             data-testid="week-zoom-controls">
          <button onClick={() => setHourPx((p) => Math.max(MIN_HOUR_PX, p - 8))}
                  className="h-6 w-6 rounded text-slate-500 hover:bg-slate-100 text-sm leading-none"
                  title="Zoom out (Ctrl/Cmd + scroll)"
                  data-testid="week-zoom-out">−</button>
          <button onClick={() => setHourPx((p) => Math.min(MAX_HOUR_PX, p + 8))}
                  className="h-6 w-6 rounded text-slate-500 hover:bg-slate-100 text-sm leading-none"
                  title="Zoom in (Ctrl/Cmd + scroll)"
                  data-testid="week-zoom-in">+</button>
        </div>
        {days.map((d, i) => {
          const isToday = sameDay(d, new Date());
          const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          const ph = publicHolidays?.find((p) => p.date === iso);
          const closure = officeClosures?.find((c) => iso >= c.start_date && iso <= c.end_date);
          // PH visual takes precedence; if only closure, show amber.
          const bg = ph ? "rgba(239,68,68,0.06)" : closure ? "rgba(245,158,11,0.08)" : undefined;
          const badgeName = ph?.name || closure?.name;
          const badgeColor = ph ? "text-red-500" : "text-amber-600";
          return (
            <div key={i}
                 className="border-r border-slate-100 last:border-r-0 px-3 py-2 flex items-center gap-1.5"
                 style={bg ? { background: bg } : undefined}
                 title={badgeName}
                 data-testid={ph ? `day-ph-${iso}` : closure ? `day-closure-${iso}` : undefined}>
              <div className={`text-2xl font-semibold leading-none ${
                isToday
                  ? "bg-[#145c8c] text-white rounded-full px-2.5 py-1.5 flex items-center justify-center shadow-sm"
                  : "text-slate-900"
              }`}
                   style={{ fontFamily: "Manrope" }}
                   data-testid={isToday ? "today-pill" : undefined}>
                {d.getDate()}
              </div>
              <div className="flex flex-col leading-tight min-w-0">
                <div className={`text-[10px] uppercase tracking-widest ${isToday ? "text-[#145c8c] font-semibold" : "text-slate-400"}`}>
                  {DAYS_LABELS[i % 7]}
                </div>
                {badgeName && (
                  <div className={`text-[10px] font-semibold truncate max-w-[110px] ${badgeColor}`}
                       title={badgeName}>
                    {badgeName}
                  </div>
                )}
              </div>
              <div className="ml-auto timer-font text-xs text-slate-400" data-testid={`day-total-${i}`}>
                {formatDuration(dayTotals[d.toDateString()] || 0)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Scrollable hour grid — stretches to fill the remaining viewport
          space. Mounted scrollTop is set to 6 AM (after layout) so the
          typical workday is visible without clipping the container. */}
      <div ref={containerRef}
           className="flex-1 min-h-0 overflow-y-auto">
        <div className="grid relative" style={{ gridTemplateColumns: gridTemplate }}>
          {/* Time column */}
          <div className="border-r border-slate-100 bg-slate-50/40">
            {HOURS.map((h) => (
              <div key={h.h} className="text-[10px] text-slate-400 pr-2 text-right select-none"
                   style={{ height: hourPx }}>
                <span className="relative -top-1.5">{h.label}</span>
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day, i) => {
            // JS getDay(): 0=Sun..6=Sat → ISO: Mon=1..Sun=7
            const iso = ((day.getDay() + 6) % 7) + 1;
            const isWorkDay = !workingDays || workingDays.includes(iso);
            return (
              <DayColumn
                key={i}
                day={day}
                dayIndex={i}
                entries={entries.filter((e) => sameDay(new Date(e.start), day) && e.end)}
                clientMap={clientMap}
                activityMap={activityMap}
                tagMap={tagMap}
                onBeginDrag={(e) => beginDrag(day, i, e)}
                dragPreview={drag && drag.dayIndex === i ? drag : null}
                onEntryClick={(en) => setEditing(en)}
                onEntryUpdate={onUpdate}
                onEntryResume={onResume}
                isLast={i === days.length - 1}
                isToday={sameDay(day, now)}
                now={now}
                runningEntry={
                  runningEntry && sameDay(new Date(runningEntry.start), day)
                    ? runningEntry
                    : null
                }
                workHours={isWorkDay ? workHours : null}
                isWorkDay={isWorkDay}
                hourPx={hourPx}
              />
            );
          })}
        </div>
      </div>

      <EntryEditor
        open={!!(editing || draft)}
        onClose={() => { setEditing(null); setDraft(null); setDrag(null); }}
        entry={editing}
        draft={draft}
        clients={clients}
        activities={activities}
        tags={tags}
        onSave={handleSave}
        onDelete={async (e) => { await onDelete(e.id); setEditing(null); setDrag(null); }}
        onCreateClient={onCreateClient}
        onCreateActivity={onCreateActivity}
      />
    </div>
  );
}

export { startOfWeek, addDays };
